import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { adminLogout, fetchCurrentAdmin } from "../lib/adminApi";

const AdminAuthContext = createContext(null);

/**
 * Separate from AuthContext (member) so both sessions can coexist. Only refreshes on mount and
 * on demand — never triggers a member `/auth/me` call.
 */
export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchCurrentAdmin();
      setAdmin(data);
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await adminLogout();
    setAdmin(null);
  }, []);

  const value = useMemo(
    () => ({ admin, loading, isAuthenticated: !!admin, refresh, logout, setAdmin }),
    [admin, loading, refresh, logout]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  return ctx;
}
